<script setup lang="ts">
import { isAxiosError } from 'axios';
import { ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';

import type { PartnerActiveCampaign, PartnerHistoryItem, RevalidaPartner } from '../types';

import PartnerActiveCampaignCard from '../components/PartnerActiveCampaignCard.vue';
import PartnerHistoryCard from '../components/PartnerHistoryCard.vue';
import PartnerKpiCards from '../components/PartnerKpiCards.vue';
import { PARTNER_JOURNEY_HISTORY_PAGE_SIZE } from '../constants';
import { mapTargetCampaignJourneyResponse } from '../mappers/partnerMappers';

const props = defineProps<{ id: string }>();

const isLoading = ref(true);
const hasError = ref(false);
const partner = ref<null | RevalidaPartner>(null);
const activeCampaign = ref<null | PartnerActiveCampaign>(null);
const history = ref<PartnerHistoryItem[]>([]);

function createEmptyPartner(id: string): RevalidaPartner {
  return {
    id,
    name: 'Parceiro não encontrado',
    revalidatedPct: null,
    revalidatedTotal: null,
    totalEligibleHistorical: null,
  };
}

async function loadPartner() {
  if (!props.id) {
    hasError.value = true;
    partner.value = createEmptyPartner('');
    activeCampaign.value = null;
    history.value = [];
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  hasError.value = false;
  activeCampaign.value = null;
  history.value = [];

  try {
    const journeyData = await campaignManagementService.getTargetCampaignJourney(props.id, {
      page: 1,
      page_size: PARTNER_JOURNEY_HISTORY_PAGE_SIZE,
    });

    const mapped = mapTargetCampaignJourneyResponse(journeyData);

    partner.value = mapped.partner;
    activeCampaign.value = mapped.activeCampaign;
    history.value = mapped.history;
    document.title = mapped.partner.name;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      partner.value = createEmptyPartner(props.id);
      activeCampaign.value = null;
      history.value = [];
      return;
    }

    hasError.value = true;
    partner.value = createEmptyPartner(props.id);
    activeCampaign.value = null;
    history.value = [];
    toast.error('Não foi possível carregar os dados do parceiro. Tente novamente.');
  } finally {
    isLoading.value = false;
  }
}

watch(() => props.id, () => {
  loadPartner();
}, { immediate: true });
</script>

<template>
  <DashboardLayout>
    <div class="partner-page-header">
      <div class="breadcrumb-back">
        <RouterLink to="/revalida-partner-view">← Parceiros</RouterLink>
      </div>
      <template v-if="isLoading">
        <div class="placeholder-glow mt-2">
          <span class="placeholder col-3" style="height: 22px; border-radius: 4px;" />
        </div>
      </template>
      <template v-else-if="partner">
        <h2>{{ partner.name }}</h2>
      </template>
    </div>

    <div v-if="hasError && !isLoading" class="error-state">
      Não foi possível carregar os dados do parceiro.
    </div>

    <template v-else>
      <PartnerKpiCards :partner="partner" :is-loading="isLoading" />
      <PartnerActiveCampaignCard :campaign="activeCampaign" :is-loading="isLoading" />
      <PartnerHistoryCard :history="history" :is-loading="isLoading" />
    </template>
  </DashboardLayout>
</template>

<style scoped lang="scss">
$verde: #00b569;
$cinza-borda: #e4e6eb;
$cinza-texto: #6b7280;
$preto: #222222;

.partner-page-header {
  margin-bottom: 20px;

  h2 {
    font-family: 'Work Sans', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: $preto;
    margin-top: 6px;
    margin-bottom: 0;
  }
}

.breadcrumb-back {
  font-size: 11px;
  color: $cinza-texto;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;

  a {
    color: $verde;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.error-state {
  background: #fff;
  border: 1px solid $cinza-borda;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  color: $cinza-texto;
  font-size: 14px;
  margin-bottom: 20px;
}
</style>
